/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useState } from "react";
import { readConfiguration } from '../../../../copilot/hooks/useTileAgentHandoff';
import type { TileAgentHandoff } from '../../../../copilot/hooks/useTileAgentHandoff';
import type { CopilotAccessMap } from '../../../../../utils/copilotAccessMap';
import { resolveTileHandoffKey } from '../../../../../utils/copilotAccessMap';
import {
  SETHasServiceRequestTypeLogData,
  SETIsCustomeLog,
  SETLogEventData,
  SETServiceRequestTypeLogData,
  setIsBotError,
  setIsLoader,
  setPreferenceChanged,
  settitleLoaderName,
  SetIsWwicCopilot,
} from "../../../../../../core/store";
import {
  WEBFORM_IRIS_UTTERANCE,
  AI_Tile_Telematry,
  Service_Tile_Telematry,
  Recommended_Tile_Details_Telematry_message,
  Catalog_Tile_Details_Panel_Start_Telematry_message,
} from "../../../../../utils/constants";

// Routing constant
const EVENT_SOURCE_DATABASE = 'database-config';

/**
 * Dependencies required by useServiceRouter — every parent-scope variable
 * the extracted routing functions referenced.
 */
export interface ServiceRouterDeps {
  // State values
  dataServiceWorkspace: any;
  webFormNames: any;
  selectedTileId: any;
  // State setters
  setAllowRendering: any;
  setOpenPanelId: any;
  setSelectedTileId: any;
  setIsTileHasForm: any;
  setIsErrorModalVisible: any;
  setSegmentDropdownVisible: any;
  setDataServiceWorkspace: any;
  setDialogTitle: any;
  setSelectedOptions: any;
  setIsDropdownModalVisible: any;
  // External functions
  handlePanelOpening: any;
  fetchTileProfile: any;
  handleStartButtonFormLogic: any;
  handleChatBotIconClick: any;
  openWebFormFinal: any;
  openWebForm: any;
  handleFormAndSegment: any;
  openIframe: any;
  normalizeString: (str: string) => string;
  // Redux
  reduxDispatch: any;
  // Per-tile copilot gating map (fetched at page load from CopilotADGroupMapping)
  copilotAccessMap: CopilotAccessMap;
  copilotAccessMapLoaded: boolean;
  loadCopilotAccessMap?: () => Promise<CopilotAccessMap>;
  onHandoffStart?: (tileId: string, config: TileAgentHandoff) => void;
  // Props subset needed by routing functions
  props: {
    setShowBot: any;
    enableWwicCopilot: boolean;
  };
}

/**
 * Extracted from V2_Tile.tsx — 10 interlinked service routing functions (Zero Logic Change).
 * Contains: openPanel, openPanelOnKeyPress, checkIrisService, ifandelsecondition,
 * openPanelForCatalogTile, IsNonIrisService, checksIris, elseSecondCondition,
 * elseCondition, elseConditionLoop, plus openWindowFrame helper and iframeUrl state.
 */
export function useServiceRouter(deps: ServiceRouterDeps) {
  const {
    dataServiceWorkspace,
    webFormNames,
    selectedTileId,
    setAllowRendering,
    setOpenPanelId,
    setSelectedTileId,
    setIsTileHasForm,
    setIsErrorModalVisible,
    setSegmentDropdownVisible,
    setDataServiceWorkspace,
    setDialogTitle,
    setSelectedOptions,
    setIsDropdownModalVisible,
    handlePanelOpening,
    fetchTileProfile,
    handleStartButtonFormLogic,
    handleChatBotIconClick,
    openWebFormFinal,
    openWebForm,
    handleFormAndSegment,
    openIframe,
    normalizeString,
    reduxDispatch,
    copilotAccessMap,
    copilotAccessMapLoaded,
    loadCopilotAccessMap,
    onHandoffStart,
    props,
  } = deps;
  const startRequest = useRef(0);
  const accessMap = useRef(copilotAccessMap);
  accessMap.current = copilotAccessMap;

  let irisBot = false;
  const [iframeUrl, setIframeUrl] = useState("");

  const openPanel = async (id, isRecommended = false, isStartClick = false) => {
    if (!isStartClick) ++startRequest.current;
    // WI-1249082 + US-1268661:
    // - Self-help (copilot) tiles always open the Service Info flyout alongside the copilot agent.
    // - enableTileForm=1 → flyout opens with Service Info default; "Create Ticket" in copilot reveals form
    // - enableTileForm=0 → flyout opens with Service Info only (no Request Details tab)
    // Copilot itself is dispatched upstream by checkIrisService (TileCard Start click) → ifandelsecondition.
    if (isStartClick) {
      const card = dataServiceWorkspace.find((srv) => srv.serviceID === id);
      const tileKey = (card?.tileName || '').toLowerCase();
      let entry = accessMap.current[tileKey];
      if (!entry?.hasAccess && card?.isRedirect && card?.redirectServiceIdForSMEC) {
        const targetCard = dataServiceWorkspace.find((srv) => srv.serviceID === card.redirectServiceIdForSMEC);
        entry = accessMap.current[(targetCard?.tileName || '').toLowerCase()];
      }
      const isSelfHelp = entry?.hasAccess === true && entry?.enableSelfHelp === true;
      if (isSelfHelp) {
        setSelectedTileId(id);
        // Open flyout with Service Info default; skip form auto-open logic.
        // When enableTileForm=false, InfoPanel defaults to "Info" tab (Service Info only).
        setAllowRendering(true);
        handlePanelOpening(id, dataServiceWorkspace, isRecommended, isStartClick);
        setOpenPanelId(id);
        return;
      }
    }

    setAllowRendering(true);
    handlePanelOpening(id, dataServiceWorkspace, isRecommended, isStartClick);
    // Fetch tile profile if exists
    const tileProfileResult = await fetchTileProfile(id);
    let redirected = false;

    if (isStartClick) {
      const card = dataServiceWorkspace.find((srv) => srv.serviceID === id);
      const tileKey = (card?.tileName || '').toLowerCase();
      // If no saved profile and tile has isSegment=true, show segment dropdown
      if (!tileProfileResult.profileJson && card?.isSegment) {
        const isAmerica = card.iriS_Utterance === WEBFORM_IRIS_UTTERANCE;
        const service = webFormNames?.find(
          (item) =>
            normalizeString(item.tileName) === normalizeString(card.tileName) &&
            Boolean(item.isAmerica) === isAmerica
        );
        const serviceWithFormKey = service?.formKey;
        // CopilotADGroupMapping.enableTileForm = 0 → webform must NOT open even if a formKey exists
        const enableTileFormFlag = accessMap.current[tileKey]?.enableTileForm;
        const isTileConsistsWebform = enableTileFormFlag !== false &&
          (serviceWithFormKey !== undefined || enableTileFormFlag === true);
        handleFormAndSegment(isTileConsistsWebform, card.name, card.iriS_Utterance, serviceWithFormKey, card.tileName);
      } else {
        // handleStartButtonFormLogic returns true if redirect happened (openPanelId already set)
        redirected = await handleStartButtonFormLogic(id, tileProfileResult);
      }
    }
    // Skip if redirect already set openPanelId to the target tile
    if (!redirected) {
      setOpenPanelId(id);
    }
  };

  const openPanelOnKeyPress = (e, id) => {
    if (e.key === "Enter") {
      openPanel(id);
    }
  };

  const openWindowFrame = (url, openinBrowserTab) => {
    if (url ? url.includes("forms") : null) {
      setIframeUrl(url);
    }
    openIframe(url, openinBrowserTab);
  };

  const checkIrisService = async (
    name,
    isNonIrisService,
    iriS_Utterance,
    serviceRequestFormLink,
    irisAppName,
    tileName,
    serviceID,
    openinBrowserTab,
    isrecommended = false,
    isSegment = false,
    appName = '',
    openCopilot = true
  ) => {
    const request = ++startRequest.current;
    setSelectedTileId(serviceID);
    // Start Event Logging //
    reduxDispatch(setPreferenceChanged(false));
    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETServiceRequestTypeLogData({
        ServiceName: name,
        RequestType: "",
        TileName: tileName,
        IsNonIrisService: isNonIrisService ? "true" : "false",
        IrisUtterance: iriS_Utterance,
        profileSetting: `v2`,
      })
    );
    const elementname = isrecommended
      ? AI_Tile_Telematry
      : Service_Tile_Telematry;
    const message = isrecommended
      ? Recommended_Tile_Details_Telematry_message + " Start"
      : Catalog_Tile_Details_Panel_Start_Telematry_message;
    reduxDispatch(
      SETLogEventData({
        elementName: elementname + "Start",
        elementId: "",
        action: "click",
        message: message,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));

    irisBot = false;
    reduxDispatch(setIsBotError(false));
    reduxDispatch(SETIsCustomeLog(true));
    // End Event Logging //
    let handoffKey = resolveTileHandoffKey(accessMap.current, tileName || '', name);
    const selectedAccess = accessMap.current[handoffKey];
    const handoffCandidate = selectedAccess?.handoffConfigured === true || selectedAccess?.handoff != null;
    if (openCopilot && loadCopilotAccessMap
        && (!copilotAccessMapLoaded || handoffCandidate || selectedAccess?.hasAccess === true)) {
      let useHandoff = false;
      let handoff: TileAgentHandoff | undefined;
      try {
        const loaded = await loadCopilotAccessMap();
        if (request !== startRequest.current) return true;
        const refreshed = Object.fromEntries(Object.entries(loaded).map(([key, entry]) => [
          key, entry.handoffSettingsUnavailable && (accessMap.current[key]?.handoffConfigured
            || accessMap.current[key]?.handoff != null) ? { ...entry, handoffConfigured: true } : entry
        ]));
        accessMap.current = refreshed;
        handoffKey = resolveTileHandoffKey(refreshed, tileName || '', name);
        const refreshedAccess = refreshed[handoffKey];
        if (refreshedAccess?.hasAccess && handoffCandidate && refreshedAccess.handoffSettingsUnavailable)
          throw new Error('Handoff presentation is unavailable.');
        if (refreshedAccess?.hasAccess
            && (refreshedAccess.handoffConfigured === true || refreshedAccess.handoff != null)) {
          const selection = readConfiguration(refreshed, handoffKey);
          if (selection.status === 'ready') {
            useHandoff = true;
            handoff = selection.config;
          }
        }
      } catch {
        console.error('Tile handoff configuration could not be loaded.');
        // Known handoffs cannot use a stale destination; unrelated legacy routes remain independent.
        useHandoff = handoffCandidate || !copilotAccessMapLoaded;
      }
      if (request !== startRequest.current) return true;
      if (useHandoff) {
        reduxDispatch(settitleLoaderName(""));
        reduxDispatch(setIsLoader(false));
        if (handoff) onHandoffStart?.(String(serviceID), handoff);
        props.setShowBot(false);
        window.dispatchEvent(new CustomEvent('open-mcaps-copilot', { detail: {
          tileName: handoffKey, serviceID, source: EVENT_SOURCE_DATABASE, appName, handoff: true
        } }));
        return true;
      }
    }
    const hasCopilotAccess = accessMap.current[(tileName || '').toLowerCase()]?.hasAccess === true;
    reduxDispatch(settitleLoaderName(hasCopilotAccess ? "" : name));
    reduxDispatch(setIsLoader(!hasCopilotAccess));
    let parsedData = null;
    if (
      serviceRequestFormLink?.startsWith("[") &&
      serviceRequestFormLink?.endsWith("]")
    ) {
      parsedData = JSON.parse(serviceRequestFormLink);
    }
    ifandelsecondition(
      name,
      isNonIrisService,
      iriS_Utterance,
      tileName,
      serviceRequestFormLink,
      parsedData,
      openinBrowserTab,
      isSegment,
      serviceID,
      irisAppName,
      appName,
      openCopilot
    );
    return false;
  };

  const ifandelsecondition = (
    name: any,
    isNonIrisService: any,
    iriS_Utterance: any,
    tileName: any,
    serviceRequestFormLink: any,
    parsedData: any,
    openinBrowserTab: any,
    isSegment: any,
    serviceID: any,
    irisAppName: any,
    appName: any,
    openCopilot: boolean
  ) => {
    // Per-tile copilot gating: use cached access map (fetched at page load)
    // Map keys are tile names (AccessPointKey in DB), not appName (which is IrisAppName)
    const tileKey = (tileName || '').toLowerCase();
    let hasCopilotAccess = accessMap.current[tileKey]?.hasAccess === true;
    // Also check redirect target — AMR Americas redirects to AMR which has copilot access
    if (!hasCopilotAccess) {
      const card = dataServiceWorkspace.find((srv) => srv.serviceID === serviceID);
      if (card?.isRedirect && card?.redirectServiceIdForSMEC) {
        const targetCard = dataServiceWorkspace.find((srv) => srv.serviceID === card.redirectServiceIdForSMEC);
        const targetKey = (targetCard?.tileName || '').toLowerCase();
        hasCopilotAccess = accessMap.current[targetKey]?.hasAccess === true;
      }
    }
    if (hasCopilotAccess) {
      const effectiveEntry = accessMap.current[tileKey] || (() => {
        const card = dataServiceWorkspace.find((srv) => srv.serviceID === serviceID);
        if (card?.isRedirect && card?.redirectServiceIdForSMEC) {
          const targetCard = dataServiceWorkspace.find((srv) => srv.serviceID === card.redirectServiceIdForSMEC);
          return accessMap.current[(targetCard?.tileName || '').toLowerCase()];
        }
        return undefined;
      })();
      const isSelfHelpTile = effectiveEntry?.enableSelfHelp === true;
      if (isSelfHelpTile) {
        // Self-help tiles do not auto-open a native Request Details form.
        setIsTileHasForm(false);
      } else if (effectiveEntry?.enableTileForm === true) {
        // Non-self-help copilot tiles: load form data so WebFormPanel renders alongside copilot
        const isAmerica = iriS_Utterance === WEBFORM_IRIS_UTTERANCE;
        const service = webFormNames?.find(
          (item) =>
            normalizeString(item.tileName) === normalizeString(tileName) &&
            Boolean(item.isAmerica) === isAmerica
        );
        if (service?.formKey !== undefined) {
          // If tile has segment, don't set isTileHasForm or open form here.
          // openPanel's handleFormAndSegment will show segment dropdown first.
          // Form will open only AFTER user saves segment (via passSegmentDataToIris).
          if (!isSegment) {
            setIsTileHasForm(true);
            openWebForm(name, iriS_Utterance, tileName, service.formKey);
          }
        }
      } else {
        // WI-1249082: reset form state so stale isTileHasForm from previous tile doesn't bleed
        setIsTileHasForm(false);
      }
      if (openCopilot) {
        props.setShowBot(false);
        const mcapsEvent = new CustomEvent('open-mcaps-copilot', {
          detail: { tileName, serviceID, source: EVENT_SOURCE_DATABASE, ...(appName && { appName }) }
        });
        window.dispatchEvent(mcapsEvent);
      }
      // External submission still uses the existing link/request-type flow, not a native form.
      if (!isNonIrisService || effectiveEntry?.enableTileForm !== false) {
        return;
      }
    }
    
    if (!isNonIrisService) {
      IsNonIrisService(name, iriS_Utterance, tileName, isSegment, irisAppName, appName);
    } else if (serviceRequestFormLink === null) {
      setIsErrorModalVisible(true);
    } else if (isNonIrisService && parsedData === null) {
      // Configurable Tiles with Segment/SubSegment Functionality:
      // For Non-IRIS tiles (IsNonIRISService = true) that have a serviceRequestFormLink and segment functionality (isSegment = true),
      // if the serviceID exists in the ConfigurableTilesList DB table, the tile's window frame actions (openWindowFrame) and 
      // segment/subsegment selection are handled in V2_DynamicDropdown.tsx instead of here.
      // This ensures that tiles requiring user preferences (stored segments/subsegments) display the dropdown first before opening the IFrame.
      // Example: "Account Management (AM Tool)" - users must select segment/subsegment before the IFrame opens.
      // Parse the cached JSON array and do an exact numeric membership check.
      // Previous implementation used String.includes() on the raw JSON blob,
      // which produced false positives whenever the serviceID appeared as a
      // substring of any other ID (e.g. "31" matched "131"). That caused the
      // Start button to silently no-op for tiles like Proactive Resiliency
      // Initiative (PRI, serviceID=31 on PROD).
      const tilesListRaw = sessionStorage.getItem("configurableTilesList");
      let parsedTilesList: any[] = [];
      try {
        parsedTilesList = tilesListRaw ? JSON.parse(tilesListRaw) : [];
      } catch {
        parsedTilesList = [];
      }
      const tileExists = Array.isArray(parsedTilesList) &&
        parsedTilesList.some(
          (t) => Number(t?.serviceID ?? t?.ServiceID ?? t?.id ?? t) === Number(serviceID)
        );
      if (tileExists === false && !isSegment) {
        openWindowFrame(serviceRequestFormLink, openinBrowserTab);
      } else if (tileExists === true && isSegment === true) {
        setTimeout(() => {
          setSegmentDropdownVisible(true);
        }, 2000);
      }
    }
    else if (
      isNonIrisService &&
      parsedData !== null &&
      parsedData.length >= 1
    ) {
      elseSecondCondition(parsedData, name);
    }
  };

  const openPanelForCatalogTile = async (workspaceTile) => {
    setAllowRendering(true);

    // Telemetry logging (mirrors handlePanelOpening but uses tile data directly)
    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETServiceRequestTypeLogData({
        ServiceName: workspaceTile.name,
        RequestType: "",
        TileName: workspaceTile.tileName,
        IsNonIrisService: workspaceTile.service_IsNonIRISService ? "true" : "false",
        IrisUtterance: workspaceTile.iriS_Utterance,
        profileSetting: "v2",
      })
    );
    reduxDispatch(
      SETLogEventData({
        elementName: "P-URL Catalog Tile Flyout",
        elementId: "",
        action: "click",
        message: `P-URL: Flyout opened from catalog for tile "${workspaceTile.tileName}" (not pinned in workspace)`,
        profileSetting: "v2",
      })
    );
    reduxDispatch(SETIsCustomeLog(true));

    // Fetch tile profile (for DefaultToRelatedInfo, segment profiles, etc.)
    const tileProfileResult = await fetchTileProfile(workspaceTile.serviceID);

    // WI-1249082 + US-1268661:
    // Self-help tiles always open Service Info flyout alongside copilot agent.
    // - enableTileForm=0 → Service Info only (no Request Details)
    // - enableTileForm=1 → Service Info default; "Create Ticket" in copilot reveals form
    const tileKey = resolveTileHandoffKey(accessMap.current, workspaceTile.tileName || '', workspaceTile.name);
    const entry = accessMap.current[tileKey];
    const isSelfHelp = entry?.hasAccess === true && entry?.enableSelfHelp === true;
    if (isSelfHelp) {
      setIsTileHasForm(false);
      props.setShowBot(false);
      const mcapsEvent = new CustomEvent('open-mcaps-copilot', {
        detail: { tileName: entry.handoffConfigured === true || entry.handoff != null ? tileKey : workspaceTile.tileName,
          serviceID: workspaceTile.serviceID, source: EVENT_SOURCE_DATABASE,
          ...(workspaceTile.appName && { appName: workspaceTile.appName }),
          ...((entry.handoffConfigured === true || entry.handoff != null) && { handoff: true }) }
      });
      window.dispatchEvent(mcapsEvent);
      // Fall through to render the Service Info flyout alongside copilot
    }

    // Inject catalog tile into workspace data so InfoPanel JSX renders, then set panel ID
    setDataServiceWorkspace(prev => [...prev, workspaceTile]);
    setOpenPanelId(workspaceTile.serviceID);
  };

  const IsNonIrisService = (
    name: string,
    iriS_Utterance: string,
    tileName: string,
    isSegment: boolean,
    irisAppName: string,
    appName: string
  ) => {
    let isTileConsistsWebform = false;

    // Use webFormNames from state; if not yet loaded (async race), fallback to localStorage
    let formList = webFormNames;
    if (!formList || formList.length === 0) {
      try {
        const envKey = process.env.REACT_APP_WEBLOCALSTORAGE_KEY || "v2_webFormNames_1.0.0";
        const stored = localStorage.getItem(envKey);
        if (stored) {
          formList = JSON.parse(stored);
        }
      } catch {
        formList = [];
      }
    }

    // Find the service based on iriS_Utterance and isAmerica flag
    const getServiceFormKey = (isAmerica: boolean) => {
      const service = formList?.find(
        (item) =>
          normalizeString(item.tileName) === normalizeString(tileName) &&
          Boolean(item.isAmerica) === isAmerica
      );
      return service?.formKey;
    };

    // Determine formKey based on iriS_Utterance
    const serviceWithFormKey =
      iriS_Utterance === WEBFORM_IRIS_UTTERANCE
        ? getServiceFormKey(true)
        : getServiceFormKey(false);

    // Check if the tileName is in the list of web form names
    if (serviceWithFormKey !== undefined) {
      isTileConsistsWebform = formList?.some(
        (item) => normalizeString(item.tileName) === normalizeString(tileName)
      );
      setIsTileHasForm(true);
    } else {
      setIsTileHasForm(false);
    }
    // Handle the web form logic
    if (isSegment) {
      handleFormAndSegment(
        isTileConsistsWebform,
        name,
        iriS_Utterance,
        serviceWithFormKey,
        tileName
      );
    } else if (isTileConsistsWebform) {
      openWebFormFinal(name, iriS_Utterance, serviceWithFormKey, tileName);
    } else if (
      tileName === "WWIC Seller" &&
      !isTileConsistsWebform &&
      props.enableWwicCopilot
    ) {
      props.setShowBot(false);
      checksIris();
      handleChatBotIconClick(iriS_Utterance, irisAppName, tileName);
      reduxDispatch(SetIsWwicCopilot(true));
    } else if (accessMap.current[(tileName || '').toLowerCase()]?.hasAccess === true) {
      // Per-tile copilot gating: use cached access map (fetched at page load)
      props.setShowBot(false);
      const mcapsEvent = new CustomEvent('open-mcaps-copilot', { 
        detail: { tileName, serviceID: selectedTileId, source: EVENT_SOURCE_DATABASE, ...(appName && { appName }) } 
      });
      window.dispatchEvent(mcapsEvent);
    } else {
      // Guard: Skip bot initialization if P-URL deferred it (prevents ghost ticket on page load)
      if (sessionStorage.getItem("PUrlBotDeferred") === "true") return;
      props.setShowBot(false);
      checksIris();
      handleChatBotIconClick(iriS_Utterance, irisAppName, tileName);
    }
  };

  const checksIris = () => {
    if (!irisBot) {
      const irisMenu = document.querySelector(".ms-layer");
      if (irisMenu?.innerHTML !== "") {
        setTimeout(() => {
          reduxDispatch(setIsLoader(false));
          irisBot = true;
        }, 10000);
      } else {
        reduxDispatch(setIsLoader(true));
        setTimeout(checksIris, 30);
      }
    }
  };
  const elseSecondCondition = (parsedData: any, name: any) => {
    let array2 = [];
    elseCondition(parsedData, array2, name);
  };

  const elseCondition = (parsedData: any, array2: any, name: any) => {
    if (parsedData) {
      elseConditionLoop(array2, parsedData);
      if (array2.length === 1) {
        openWindowFrame(
          array2[0].value,
          array2[0].openinBrowserTab
        );
      } else if (array2.length > 1) {
        setDialogTitle(name);
        setSelectedOptions(array2);
        // delay segment popup by 1700ms
        setTimeout(() => {
          setIsDropdownModalVisible(true);
        }, 1700);
      }
    }
  };
  const elseConditionLoop = (array2: any, parsedData: any) => {
    for (let i = 0; i < parsedData.length; i++) {
      let e = parsedData[i];
      if (e.RequestTypeName === "N/A" && e.ServiceRequestFormLink !== null) {
        openWindowFrame(e.ServiceRequestFormLink, e.OpeninBrowserTab);
        array2.length = 0;
        break;
      } else if (
        e.RequestTypeName !== "N/A" &&
        e.RequestTypeName !== null &&
        e.ServiceRequestFormLink === null
      ) {
        continue;
      } else if (
        (e.RequestTypeName === null || e.RequestTypeName === "N/A") &&
        e.ServiceRequestFormLink === null
      ) {
        continue;
      } else {
        array2.push({
          key: e.RequestTypeName + i,
          text: e.RequestTypeName,
          value: e.ServiceRequestFormLink,
          openinBrowserTab: e.OpeninBrowserTab,
        });
      }
    }
  };

  return {
    checkIrisService,
    openPanel,
    openPanelOnKeyPress,
    openPanelForCatalogTile,
    openWindowFrame,
    checksIris,
    elseConditionLoop,
  };
}
// Generated by Copilot | 2026-10-26 | 28 lines added/updated | Auto | 1321277 suppress legacy submission for approved handoffs and ignore stale Starts | Time Taken 8 min
// Generated by Copilot | 2026-10-26 | 6 lines added/updated | Auto | 1321278 emit configured Start telemetry only from the click routing path | Time Taken 3 min
// Generated by Copilot | 2026-10-26 | 42 lines added/updated/removed | Auto | 1322441 refresh existing access map before routing and reject stale Start decisions | Time Taken 8 min
// Generated by Copilot | 2026-10-27 | 6 lines added/updated/removed | Auto | 1322441 refresh configured handoffs without blocking unrelated cached tile routes | Time Taken 4 min
// Generated by Copilot | 2026-10-27 | 5 lines added/updated/removed | Auto | 1322441 retain handoff context when the catalog self-help helper opens the rail | Time Taken 2 min
// Generated by Copilot | 2026-10-27 | 17 lines added/updated/removed | Auto | 1322441 resolve handoff identity before refresh and carry its access-point key into the panel | Time Taken 4 min
// Generated by Copilot | 2026-10-29 | 20 lines added/updated/removed | Auto | Discover new authorized handoffs on Start while isolating legacy routes from presentation failures | Time Taken 8 min
